import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { invoicesApi, clientsApi, bankAccountsApi, currencySymbols, formatInvoiceDate, dateFormatForRegion, parseInvoiceDate } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { Plus, Trash2, ArrowLeft, FileText, CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';

const CreateInvoicePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEditing = Boolean(id);

  const [clients, setClients] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    client_id: '',
    bank_account_id: '',
    invoice_date: format(new Date(), 'dd/MM/yyyy'),
    invoice_date_obj: new Date(),
    currency: 'USD',
    notes: '',
    items: [{ description: '', amount: 0 }],
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [clientsRes, banksRes] = await Promise.all([
        clientsApi.getAll(),
        bankAccountsApi.getAll()
      ]);
      setClients(clientsRes.data);
      setBankAccounts(banksRes.data);

      if (isEditing) {
        const invoiceRes = await invoicesApi.get(id);
        const invoice = invoiceRes.data;
        setFormData({
          client_id: invoice.client_id,
          bank_account_id: invoice.bank_account_id,
          invoice_date: invoice.invoice_date,
          invoice_date_obj: parseInvoiceDate(invoice.invoice_date) || new Date(),
          currency: invoice.currency,
          notes: invoice.notes,
          items: invoice.items.length > 0 ? invoice.items : [{ description: '', amount: 0 }],
        });
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleClientChange = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (client) {
      // Find matching bank account for client region
      const matchingBank = bankAccounts.find(b => b.region === client.region);
      const regionCurrency = { US: 'USD', UK: 'GBP', EU: 'EUR', IN: 'INR', CA: 'CAD' };
      
      setFormData({
        ...formData,
        client_id: clientId,
        bank_account_id: matchingBank?.id || formData.bank_account_id,
        currency: regionCurrency[client.region] || 'USD',
      });
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: field === 'amount' ? parseFloat(value) || 0 : value };
    setFormData({ ...formData, items: newItems });
  };

  const addItem = () => {
    setFormData({ ...formData, items: [...formData.items, { description: '', amount: 0 }] });
  };

  const removeItem = (index) => {
    if (formData.items.length === 1) return;
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const calculateTotal = () => {
    return formData.items.reduce((sum, item) => sum + (item.amount || 0), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.client_id) {
      toast.error('Please select a client');
      return;
    }
    if (!formData.bank_account_id) {
      toast.error('Please select a bank account');
      return;
    }
    if (formData.items.every(item => !item.description && item.amount === 0)) {
      toast.error('Please add at least one item');
      return;
    }

    setSaving(true);
    try {
      if (isEditing) {
        await invoicesApi.update(id, formData);
        toast.success('Invoice updated');
      } else {
        const response = await invoicesApi.create(formData);
        toast.success('Invoice created');
        navigate(`/invoices/${response.data.id}`);
        return;
      }
      navigate('/invoices');
    } catch (error) {
      toast.error('Failed to save invoice');
    } finally {
      setSaving(false);
    }
  };

  const currencySymbol = currencySymbols[formData.currency] || '$';

  if (loading) {
    return (
      <div className="p-8 animate-pulse">
        <div className="h-8 w-48 bg-zinc-200 rounded mb-8" />
        <div className="h-96 bg-zinc-100 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate('/invoices')}
          data-testid="back-to-invoices-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="font-heading text-2xl font-bold text-zinc-900">
            {isEditing ? 'Edit Invoice' : 'Create New Invoice'}
          </h1>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="max-w-4xl">
        <div className="bg-white rounded-xl border border-zinc-200 p-8 space-y-8">
          {/* Client & Bank Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="client">Client *</Label>
              {clients.length === 0 ? (
                <div className="p-4 bg-zinc-50 rounded-lg text-center">
                  <p className="text-sm text-zinc-500 mb-2">No clients yet</p>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigate('/clients')}
                  >
                    Add Client
                  </Button>
                </div>
              ) : (
                <Select 
                  value={formData.client_id} 
                  onValueChange={handleClientChange}
                >
                  <SelectTrigger data-testid="invoice-client-select">
                    <SelectValue placeholder="Select a client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name} ({client.region})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="bank">Bank Account *</Label>
              {bankAccounts.length === 0 ? (
                <div className="p-4 bg-zinc-50 rounded-lg text-center">
                  <p className="text-sm text-zinc-500 mb-2">No bank accounts yet</p>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigate('/bank-accounts')}
                  >
                    Add Bank Account
                  </Button>
                </div>
              ) : (
                <Select 
                  value={formData.bank_account_id} 
                  onValueChange={(value) => setFormData({ ...formData, bank_account_id: value })}
                >
                  <SelectTrigger data-testid="invoice-bank-select">
                    <SelectValue placeholder="Select bank account" />
                  </SelectTrigger>
                  <SelectContent>
                    {bankAccounts.map((bank) => (
                      <SelectItem key={bank.id} value={bank.id}>
                        {bank.account_name} ({bank.region} - {bank.currency})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          {/* Date & Currency */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="invoice_date">
                Invoice Date
                {(() => {
                  const c = clients.find(cl => cl.id === formData.client_id);
                  const r = c?.region;
                  return r ? (
                    <span className="ml-2 text-xs text-zinc-400 font-normal">
                      Display format for {r}: {dateFormatForRegion(r)}
                    </span>
                  ) : null;
                })()}
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    id="invoice_date"
                    type="button"
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-mono h-10",
                      !formData.invoice_date_obj && "text-zinc-400"
                    )}
                    data-testid="invoice-date-input"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 text-zinc-500" />
                    {formData.invoice_date_obj ? (
                      (() => {
                        const c = clients.find(cl => cl.id === formData.client_id);
                        return formatInvoiceDate(formData.invoice_date_obj, c?.region);
                      })()
                    ) : (
                      <span>Pick a date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start" data-testid="invoice-date-calendar">
                  <Calendar
                    mode="single"
                    selected={formData.invoice_date_obj}
                    onSelect={(date) => {
                      if (!date) return;
                      setFormData({
                        ...formData,
                        invoice_date_obj: date,
                        // Store as DD/MM/YYYY for backend compatibility
                        invoice_date: format(date, 'dd/MM/yyyy'),
                      });
                    }}
                    initialFocus
                    captionLayout="dropdown-buttons"
                    fromYear={2020}
                    toYear={2035}
                    classNames={{
                      caption: "flex justify-center pt-1 pb-2 relative items-center",
                      caption_label: "hidden",
                      caption_dropdowns: "flex gap-2 items-center",
                      dropdown: "text-sm font-medium bg-white border border-zinc-200 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-orange-500 cursor-pointer",
                      dropdown_month: "",
                      dropdown_year: "",
                      vhidden: "hidden",
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label htmlFor="currency">Currency</Label>
              <Select 
                value={formData.currency} 
                onValueChange={(value) => setFormData({ ...formData, currency: value })}
              >
                <SelectTrigger data-testid="invoice-currency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD ($)</SelectItem>
                  <SelectItem value="GBP">GBP (£)</SelectItem>
                  <SelectItem value="EUR">EUR (€)</SelectItem>
                  <SelectItem value="INR">INR (₹)</SelectItem>
                  <SelectItem value="CAD">CAD (C$)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Line Items */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Line Items</Label>
              <Button 
                type="button" 
                variant="outline" 
                size="sm" 
                onClick={addItem}
                data-testid="add-item-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Item
              </Button>
            </div>

            <div className="space-y-3">
              {formData.items.map((item, index) => (
                <div 
                  key={index} 
                  className="flex items-center gap-4 p-4 bg-zinc-50 rounded-lg"
                  data-testid={`invoice-item-${index}`}
                >
                  <div className="flex-1">
                    <Input
                      value={item.description}
                      onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                      placeholder="Service description..."
                      data-testid={`item-description-${index}`}
                    />
                  </div>
                  <div className="w-40 flex items-center">
                    <span className="text-zinc-400 font-mono mr-2">{currencySymbol}</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={item.amount || ''}
                      onChange={(e) => handleItemChange(index, 'amount', e.target.value)}
                      placeholder="0.00"
                      className="font-mono text-right"
                      data-testid={`item-amount-${index}`}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeItem(index)}
                    disabled={formData.items.length === 1}
                    className="text-zinc-400 hover:text-red-500"
                    data-testid={`remove-item-${index}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>

            {/* Total */}
            <div className="flex justify-end pt-4 border-t border-zinc-100">
              <div className="text-right">
                <p className="text-sm text-zinc-500 mb-1">Total</p>
                <p className="text-3xl font-bold font-mono text-zinc-900" data-testid="invoice-total">
                  {currencySymbol}{calculateTotal().toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any additional notes for the invoice..."
              rows={3}
              data-testid="invoice-notes-input"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-4 pt-4 border-t border-zinc-100">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => navigate('/invoices')}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={saving}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="save-invoice-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : isEditing ? 'Update Invoice' : 'Create Invoice'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreateInvoicePage;
